import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "@/lib/api";
import { toast } from "sonner";
import { 
  ArrowRight,
  ArrowDown,
  Users,
  Building2,
  ShoppingCart,
  CheckCircle,
  AlertCircle,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  GitBranch,
  Plus,
  Edit2,
  Trash2,
  Copy,
  Save,
  X
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import MermaidDiagram from "@/components/MermaidDiagram";

const actorIcons = {
  seller: Users,
  ar: Building2,
  marketplace: ShoppingCart,
  both: Users
};

const actorColors = {
  seller: "bg-blue-100 text-blue-700 border-blue-200",
  ar: "bg-violet-100 text-violet-700 border-violet-200",
  marketplace: "bg-orange-100 text-orange-700 border-orange-200",
  both: "bg-[#F3F2EE] text-[#707070] border-[#E5E5E1]"
};

// Mini flow diagrams for each model
const MODEL_DIAGRAMS = {
  ar_led: `flowchart LR
    S[Seller] -->|Mandate| AR[AR]
    AR -->|Register| REG[(Registers)]
    AR -->|Join| PRO[(PRO)]
    S -->|Pay| AR`,
  marketplace_managed: `flowchart LR
    S[Seller] -->|Opt-in| MP[Marketplace]
    MP -->|Register| REG[(Registers)]
    MP -->|Report| PRO[(PRO)]
    MP -->|Dashboard| S`,
  pay_on_behalf: `flowchart LR
    S[Seller] -->|Sell| MP[Marketplace]
    MP -->|Pay covered| PRO[(PRO)]
    MP -->|Deduct| S
    S -.->|Pay non-covered| REG[(Registers)]`
};

// Empty model template
const EMPTY_MODEL = {
  name: "",
  short_name: "",
  description: "",
  best_for: [],
  complexity: "medium",
  seller_involvement: "medium",
  cost_level: "medium",
  steps: [],
  rules: [],
  seller_responsibilities: [],
  ar_responsibilities: [],
  marketplace_responsibilities: [],
  important_note: ""
};

// Empty step template
const EMPTY_STEP = {
  id: "",
  name: "",
  description: "",
  actor: "seller"
};

// Empty rule template
const EMPTY_RULE = {
  id: "",
  category: "",
  rule: "",
  applies_to: ""
};

export default function ComplianceModels() {
  const navigate = useNavigate();
  const api = useApi();
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRecommender, setShowRecommender] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const [recommendLoading, setRecommendLoading] = useState(false);
  
  // Edit/Create state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [formData, setFormData] = useState(EMPTY_MODEL);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Recommender form state
  const [isEuBased, setIsEuBased] = useState(false);
  const [primaryMarketplace, setPrimaryMarketplace] = useState("");
  const [annualVolume, setAnnualVolume] = useState("medium");
  const [complianceExpertise, setComplianceExpertise] = useState("basic");
  const [multiMarketplace, setMultiMarketplace] = useState(false);

  useEffect(() => {
    fetchModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchModels = async () => {
    try {
      // Try the new endpoint first (includes custom models)
      const response = await api.get("/api/custom-compliance-models");
      setModels(response.data.models || response.data);
      if ((response.data.models || response.data).length > 0) {
        setSelectedModel((response.data.models || response.data)[0]);
      }
    } catch (error) {
      // Fallback to old endpoint
      try {
        const response = await api.get("/api/compliance-models");
        const modelsWithFlag = (response.data || []).map(m => ({...m, is_default: true, is_custom: false}));
        setModels(modelsWithFlag);
        if (modelsWithFlag.length > 0) {
          setSelectedModel(modelsWithFlag[0]);
        }
      } catch (err) {
        console.error("Error fetching models:", err);
        toast.error("Failed to load compliance models");
      }
    } finally {
      setLoading(false);
    }
  };

  const getRecommendation = async () => {
    setRecommendLoading(true);
    try {
      const response = await api.post("/api/compliance-models/recommend", {
        is_eu_based: isEuBased,
        primary_marketplace: primaryMarketplace || null,
        annual_volume: annualVolume,
        compliance_expertise: complianceExpertise,
        multi_marketplace: multiMarketplace
      });
      setRecommendation(response.data);
      toast.success("Recommendation generated");
    } catch (error) {
      console.error("Error getting recommendation:", error);
      toast.error("Failed to get recommendation");
    } finally {
      setRecommendLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingModel(null);
    setFormData({...EMPTY_MODEL, best_for: [], steps: [], rules: [], seller_responsibilities: [], ar_responsibilities: [], marketplace_responsibilities: []});
    setShowEditDialog(true);
  };

  const openEditDialog = (model) => {
    if (model.is_default && !model.is_custom) {
      toast.error("Cannot edit default models. Duplicate it first to create your own version.");
      return;
    }
    setEditingModel(model);
    setFormData({
      name: model.name || "",
      short_name: model.short_name || "",
      description: model.description || "",
      best_for: model.best_for || [],
      complexity: model.complexity || "medium",
      seller_involvement: model.seller_involvement || "medium",
      cost_level: model.cost_level || "medium",
      steps: model.steps || [],
      rules: model.rules || [],
      seller_responsibilities: model.seller_responsibilities || [],
      ar_responsibilities: model.ar_responsibilities || [],
      marketplace_responsibilities: model.marketplace_responsibilities || [],
      important_note: model.important_note || ""
    });
    setShowEditDialog(true);
  };

  const duplicateModel = async (model) => {
    try {
      const response = await api.post("/api/custom-compliance-models/duplicate", {
        source_model_id: model.id,
        new_name: `${model.name} (Copy)`
      });
      toast.success("Model duplicated successfully");
      await fetchModels();
      setSelectedModel(response.data);
    } catch (error) {
      console.error("Error duplicating model:", error);
      toast.error("Failed to duplicate model");
    }
  };

  const saveModel = async () => {
    if (!formData.name.trim()) {
      toast.error("Model name is required");
      return;
    }

    setSaving(true);
    try {
      if (editingModel) {
        // Update existing
        await api.put(`/api/custom-compliance-models/${editingModel.id}`, formData);
        toast.success("Model updated successfully");
      } else {
        // Create new
        await api.post("/api/custom-compliance-models", formData);
        toast.success("Model created successfully");
      }
      setShowEditDialog(false);
      await fetchModels();
    } catch (error) {
      console.error("Error saving model:", error);
      toast.error("Failed to save model");
    } finally {
      setSaving(false);
    }
  };

  const deleteModel = async (model) => {
    if (model.is_default && !model.is_custom) {
      toast.error("Cannot delete default models");
      return;
    }
    try {
      await api.delete(`/api/custom-compliance-models/${model.id}`);
      toast.success("Model deleted");
      setDeleteConfirm(null);
      await fetchModels();
    } catch (error) {
      console.error("Error deleting model:", error);
      toast.error("Failed to delete model");
    }
  };

  // Form helpers
  const addStep = () => {
    const newId = String.fromCharCode(65 + formData.steps.length); // A, B, C...
    setFormData({
      ...formData,
      steps: [...formData.steps, {...EMPTY_STEP, id: newId}]
    });
  };

  const updateStep = (index, field, value) => {
    const updated = [...formData.steps];
    updated[index] = {...updated[index], [field]: value};
    setFormData({...formData, steps: updated});
  };

  const removeStep = (index) => {
    setFormData({
      ...formData,
      steps: formData.steps.filter((_, i) => i !== index)
    });
  };

  const addRule = () => {
    const newId = `R${formData.rules.length + 1}`;
    setFormData({
      ...formData,
      rules: [...formData.rules, {...EMPTY_RULE, id: newId}]
    });
  };

  const updateRule = (index, field, value) => {
    const updated = [...formData.rules];
    updated[index] = {...updated[index], [field]: value};
    setFormData({...formData, rules: updated});
  };

  const removeRule = (index) => {
    setFormData({
      ...formData,
      rules: formData.rules.filter((_, i) => i !== index)
    });
  };

  const updateListField = (field, value) => {
    // Convert newline-separated text to array
    const items = value.split("\n").filter(item => item.trim());
    setFormData({...formData, [field]: items});
  };

  if (loading) {
    return (
      <div className="content-wrapper flex items-center justify-center min-h-[50vh]">
        <div className="loading-spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="content-wrapper" data-testid="compliance-models-page">
      <div className="page-header flex justify-between items-start">
        <div>
          <h1 className="page-title">Compliance Service Models</h1>
          <p className="page-subtitle">
            Understand and compare different EPR compliance approaches for marketplace sellers
          </p>
        </div>
        <Button onClick={openCreateDialog} data-testid="create-model-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Model
        </Button>
      </div>

      {/* Model Selector Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {models.map((model) => (
          <button
            key={model.id}
            onClick={() => setSelectedModel(model)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              selectedModel?.id === model.id
                ? "bg-[#E85C33] text-white"
                : "bg-white border border-[#E5E5E1] text-[#1A1A1B] hover:border-[#E85C33]"
            } ${model.is_custom ? "border-dashed" : ""}`}
            data-testid={`model-tab-${model.id}`}
          >
            {model.short_name || model.name}
            {model.is_custom && <span className="ml-1 text-xs">(Custom)</span>}
          </button>
        ))}
        <button
          onClick={() => setShowRecommender(!showRecommender)}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ml-auto ${
            showRecommender
              ? "bg-green-600 text-white"
              : "bg-green-50 border border-green-200 text-green-700 hover:bg-green-100"
          }`}
          data-testid="show-recommender-btn"
        >
          <HelpCircle className="inline w-4 h-4 mr-1" />
          Find Best Model
        </button>
      </div>

      {/* Recommender Panel */}
      {showRecommender && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6 animate-fade-in">
          <h3 className="text-lg font-semibold text-[#1A1A1B] mb-4">Model Recommender</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <div>
              <Label className="text-sm mb-2 block">EU-Based Seller?</Label>
              <div className="flex items-center gap-2">
                <Switch checked={isEuBased} onCheckedChange={setIsEuBased} />
                <span className="text-sm text-[#707070]">{isEuBased ? "Yes" : "No"}</span>
              </div>
            </div>
            <div>
              <Label className="text-sm mb-2 block">Multi-Marketplace?</Label>
              <div className="flex items-center gap-2">
                <Switch checked={multiMarketplace} onCheckedChange={setMultiMarketplace} />
                <span className="text-sm text-[#707070]">{multiMarketplace ? "Yes" : "No"}</span>
              </div>
            </div>
            <div>
              <Label className="text-sm mb-2 block">Primary Marketplace</Label>
              <Select value={primaryMarketplace} onValueChange={setPrimaryMarketplace}>
                <SelectTrigger>
                  <SelectValue placeholder="Select marketplace" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="amazon">Amazon</SelectItem>
                  <SelectItem value="temu">Temu</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm mb-2 block">Annual Volume</Label>
              <Select value={annualVolume} onValueChange={setAnnualVolume}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low (&lt;€100k)</SelectItem>
                  <SelectItem value="medium">Medium (€100k-€1M)</SelectItem>
                  <SelectItem value="high">High (&gt;€1M)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm mb-2 block">Compliance Expertise</Label>
              <Select value={complianceExpertise} onValueChange={setComplianceExpertise}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button 
                onClick={getRecommendation} 
                disabled={recommendLoading}
                className="w-full"
                data-testid="get-recommendation-btn"
              >
                {recommendLoading ? "Analyzing..." : "Get Recommendation"}
              </Button>
            </div>
          </div>

          {recommendation && (
            <div className="mt-4 p-4 bg-white rounded-xl border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-semibold text-[#1A1A1B]">
                  Recommended: {recommendation.model_details.name}
                </span>
                <Badge className="bg-green-100 text-green-700">
                  Score: {recommendation.confidence_score}
                </Badge>
              </div>
              <ul className="text-sm text-[#707070] space-y-1">
                {recommendation.reasoning.map((reason, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-1.5 flex-shrink-0"></span>
                    {reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {selectedModel && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Model Overview */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white border border-[#E5E5E1] rounded-xl p-6">
              <div className="flex items-start justify-between mb-2">
                <h2 className="text-xl font-semibold text-[#1A1A1B]">{selectedModel.name}</h2>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => duplicateModel(selectedModel)}
                    title="Duplicate"
                    data-testid="duplicate-model-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  {selectedModel.is_custom && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(selectedModel)}
                        title="Edit"
                        data-testid="edit-model-btn"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteConfirm(selectedModel)}
                        title="Delete"
                        className="text-red-500 hover:text-red-600"
                        data-testid="delete-model-btn"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {selectedModel.is_custom && (
                <Badge className="mb-2 bg-[#FDEAE5] text-[#E85C33]">Custom Model</Badge>
              )}
              <p className="text-sm text-[#707070] mb-4">{selectedModel.description}</p>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#707070]">Complexity</span>
                  <Badge variant="secondary">{selectedModel.complexity}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#707070]">Seller Involvement</span>
                  <Badge variant="secondary">{selectedModel.seller_involvement}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#707070]">Cost Level</span>
                  <Badge variant="secondary">{selectedModel.cost_level}</Badge>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-[#E5E5E1]">
                <h4 className="text-sm font-medium text-[#1A1A1B] mb-2">Best For:</h4>
                <ul className="text-sm text-[#707070] space-y-1">
                  {selectedModel.best_for?.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Mini Flow Diagram */}
              {MODEL_DIAGRAMS[selectedModel.id] && (
                <div className="mt-4 pt-4 border-t border-[#E5E5E1]">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-[#1A1A1B]">Quick Flow</h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate('/business-flows')}
                      className="text-xs"
                    >
                      <GitBranch className="w-3 h-3 mr-1" />
                      Full Diagram
                    </Button>
                  </div>
                  <div className="bg-[#FBF9F4] rounded-xl p-3 overflow-hidden">
                    <MermaidDiagram chart={MODEL_DIAGRAMS[selectedModel.id]} className="scale-90 origin-left" />
                  </div>
                </div>
              )}
            </div>

            {/* Responsibilities */}
            <div className="bg-white border border-[#E5E5E1] rounded-xl p-6">
              <h3 className="text-sm font-semibold text-[#707070] uppercase tracking-wide mb-4">
                Responsibilities
              </h3>
              <Accordion type="multiple" className="space-y-2">
                <AccordionItem value="seller" className="border border-[#E5E5E1] rounded-xl px-3">
                  <AccordionTrigger className="hover:no-underline py-2">
                    <span className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium">Seller</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-3">
                    <ul className="text-sm text-[#707070] space-y-1">
                      {selectedModel.seller_responsibilities?.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="w-1 h-1 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
                
                {selectedModel.ar_responsibilities?.length > 0 && (
                  <AccordionItem value="ar" className="border border-[#E5E5E1] rounded-xl px-3">
                    <AccordionTrigger className="hover:no-underline py-2">
                      <span className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-violet-600" />
                        <span className="text-sm font-medium">Authorized Representative</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-3">
                      <ul className="text-sm text-[#707070] space-y-1">
                        {selectedModel.ar_responsibilities.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="w-1 h-1 bg-violet-500 rounded-full mt-2 flex-shrink-0"></span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                )}
                
                {selectedModel.marketplace_responsibilities?.length > 0 && (
                  <AccordionItem value="marketplace" className="border border-[#E5E5E1] rounded-xl px-3">
                    <AccordionTrigger className="hover:no-underline py-2">
                      <span className="flex items-center gap-2">
                        <ShoppingCart className="w-4 h-4 text-orange-600" />
                        <span className="text-sm font-medium">Marketplace</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-3">
                      <ul className="text-sm text-[#707070] space-y-1">
                        {selectedModel.marketplace_responsibilities.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="w-1 h-1 bg-orange-500 rounded-full mt-2 flex-shrink-0"></span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                )}
              </Accordion>
            </div>
          </div>

          {/* Process Flow */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-[#E5E5E1] rounded-xl p-6">
              <h3 className="text-lg font-semibold text-[#1A1A1B] mb-6">Process Flow</h3>
              
              {/* Main Flow */}
              <div className="space-y-3">
                {selectedModel.steps?.map((step, idx) => {
                  const ActorIcon = actorIcons[step.actor] || Users;
                  const isLast = idx === selectedModel.steps.length - 1;
                  
                  return (
                    <div key={step.id}>
                      <div className={`flex items-start gap-4 p-4 rounded-xl border ${actorColors[step.actor]}`}>
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white flex items-center justify-center font-semibold text-sm">
                          {step.id}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-[#1A1A1B]">{step.name}</span>
                            <Badge variant="outline" className="text-xs capitalize">
                              <ActorIcon className="w-3 h-3 mr-1" />
                              {step.actor}
                            </Badge>
                          </div>
                          <p className="text-sm text-[#707070]">{step.description}</p>
                        </div>
                      </div>
                      {!isLast && (
                        <div className="flex justify-center py-1">
                          <ArrowDown className="w-4 h-4 text-[#E5E5E1]" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Parallel Steps for Pay-on-Behalf */}
              {selectedModel.seller_parallel_steps && (
                <div className="mt-6 pt-6 border-t border-[#E5E5E1]">
                  <div className="flex items-center gap-2 mb-4">
                    <AlertCircle className="w-5 h-5 text-amber-500" />
                    <h4 className="font-semibold text-[#1A1A1B]">Seller Parallel Obligations</h4>
                  </div>
                  <p className="text-sm text-[#707070] mb-4">
                    These steps run in parallel - seller must handle non-covered obligations:
                  </p>
                  <div className="space-y-3">
                    {selectedModel.seller_parallel_steps.map((step, idx) => {
                      const isLast = idx === selectedModel.seller_parallel_steps.length - 1;
                      return (
                        <div key={step.id}>
                          <div className="flex items-start gap-4 p-4 rounded-xl border border-amber-200 bg-amber-50">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white flex items-center justify-center font-semibold text-sm text-amber-700">
                              {step.id}
                            </div>
                            <div className="flex-1">
                              <span className="font-medium text-[#1A1A1B]">{step.name}</span>
                              <p className="text-sm text-[#707070]">{step.description}</p>
                            </div>
                          </div>
                          {!isLast && (
                            <div className="flex justify-center py-1">
                              <ArrowDown className="w-4 h-4 text-amber-300" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Important Note */}
              {selectedModel.important_note && (
                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">{selectedModel.important_note}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Rules */}
            <div className="bg-white border border-[#E5E5E1] rounded-xl p-6 mt-6">
              <h3 className="text-lg font-semibold text-[#1A1A1B] mb-4">Compliance Rules</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {selectedModel.rules?.map((rule) => (
                  <div key={rule.id} className="p-3 bg-[#FBF9F4] rounded-xl border border-[#E5E5E1]">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="secondary" className="text-xs">{rule.id}</Badge>
                      <span className="font-medium text-sm text-[#1A1A1B]">{rule.category}</span>
                    </div>
                    <p className="text-sm text-[#707070]">{rule.rule}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingModel ? "Edit Compliance Model" : "Create Compliance Model"}</DialogTitle>
            <DialogDescription>
              Define your custom compliance service model with steps, rules, and responsibilities.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 sm:col-span-1">
                <Label>Model Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Hybrid Compliance Model"
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Label>Short Name</Label>
                <Input
                  value={formData.short_name}
                  onChange={(e) => setFormData({...formData, short_name: e.target.value})}
                  placeholder="e.g., Hybrid"
                />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Describe the compliance model..."
                  rows={2}
                />
              </div>
            </div>

            {/* Attributes */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Complexity</Label>
                <Select value={formData.complexity} onValueChange={(v) => setFormData({...formData, complexity: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Seller Involvement</Label>
                <Select value={formData.seller_involvement} onValueChange={(v) => setFormData({...formData, seller_involvement: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cost Level</Label>
                <Select value={formData.cost_level} onValueChange={(v) => setFormData({...formData, cost_level: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="medium-high">Medium-High</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="variable">Variable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Best For */}
            <div>
              <Label>Best For (one per line)</Label>
              <Textarea
                value={formData.best_for?.join("\n") || ""}
                onChange={(e) => updateListField("best_for", e.target.value)}
                placeholder="Non-EU sellers&#10;High-volume sellers&#10;..."
                rows={3}
              />
            </div>

            {/* Steps */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Process Steps</Label>
                <Button variant="outline" size="sm" onClick={addStep}>
                  <Plus className="w-3 h-3 mr-1" /> Add Step
                </Button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {formData.steps.map((step, idx) => (
                  <div key={idx} className="flex gap-2 items-start p-2 bg-[#FBF9F4] rounded-lg">
                    <Input
                      value={step.id}
                      onChange={(e) => updateStep(idx, "id", e.target.value)}
                      placeholder="ID"
                      className="w-16"
                    />
                    <Input
                      value={step.name}
                      onChange={(e) => updateStep(idx, "name", e.target.value)}
                      placeholder="Step name"
                      className="flex-1"
                    />
                    <Select value={step.actor} onValueChange={(v) => updateStep(idx, "actor", v)}>
                      <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="seller">Seller</SelectItem>
                        <SelectItem value="ar">AR</SelectItem>
                        <SelectItem value="marketplace">Marketplace</SelectItem>
                        <SelectItem value="both">Both</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" onClick={() => removeStep(idx)} className="text-red-500">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Responsibilities */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Seller Responsibilities (one per line)</Label>
                <Textarea
                  value={formData.seller_responsibilities?.join("\n") || ""}
                  onChange={(e) => updateListField("seller_responsibilities", e.target.value)}
                  placeholder="Provide data&#10;Pay fees&#10;..."
                  rows={4}
                />
              </div>
              <div>
                <Label>AR Responsibilities (one per line)</Label>
                <Textarea
                  value={formData.ar_responsibilities?.join("\n") || ""}
                  onChange={(e) => updateListField("ar_responsibilities", e.target.value)}
                  placeholder="Register producer&#10;Submit filings&#10;..."
                  rows={4}
                />
              </div>
              <div>
                <Label>Marketplace Responsibilities (one per line)</Label>
                <Textarea
                  value={formData.marketplace_responsibilities?.join("\n") || ""}
                  onChange={(e) => updateListField("marketplace_responsibilities", e.target.value)}
                  placeholder="Provide dashboard&#10;Collect fees&#10;..."
                  rows={4}
                />
              </div>
            </div>

            {/* Important Note */}
            <div>
              <Label>Important Note (optional)</Label>
              <Textarea
                value={formData.important_note || ""}
                onChange={(e) => setFormData({...formData, important_note: e.target.value})}
                placeholder="Any critical information about this model..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={saveModel} disabled={saving}>
              {saving ? "Saving..." : (editingModel ? "Update Model" : "Create Model")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Compliance Model?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteConfirm?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteModel(deleteConfirm)} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
